/**
 * Record-filter engine — the framework-free core of the leader filter bars.
 *
 * A surface (Item Scorer Summary, Responses / Backreading, …) supplies a **field catalogue**:
 * what each field is called, what kind of value it reads off a row, and which range types it
 * offers. The engine owns everything that is the same for every surface — the operator table,
 * whether a condition is complete enough to filter, evaluating a condition against a row,
 * describing it for a chip, and folding several conditions through And / Or.
 *
 * Nothing here knows about scorers, batches or responses. `scorerStatsFilters.ts` binds its own
 * catalogue and re-exports the names it used to define, so existing callers are unchanged.
 */

export type FilterJoinMode = 'AND' | 'OR';

export type ConditionOp =
  // numeric (metric) and date
  | 'gte' | 'gt' | 'lte' | 'lt' | 'eq' | 'between' | 'outside' | 'empty' | 'notEmpty'
  // items (sets held by the row's owner)
  | 'includesAny' | 'includesAll' | 'excludesAny'
  // flag / enum
  | 'is' | 'isNot' | 'isAnyOf'
  // text
  | 'contains' | 'notContains'
  // date
  | 'on' | 'before' | 'after';

export interface ConditionOpDef {
  id: ConditionOp;
  /** Long form for the operator picker. */
  label: string;
  /** Short form for chips and the criteria line. */
  symbol: string;
  /** How many values the operator consumes. */
  args: 0 | 1 | 2;
}

export const CONDITION_OPS: {[id: string]: ConditionOpDef} = {
  gte:         {id:'gte',         label:'is at least',      symbol:'≥',                args:1},
  gt:          {id:'gt',          label:'is more than',     symbol:'>',                args:1},
  lte:         {id:'lte',         label:'is at most',       symbol:'≤',                args:1},
  lt:          {id:'lt',          label:'is less than',     symbol:'<',                args:1},
  eq:          {id:'eq',          label:'equals',           symbol:'=',                args:1},
  between:     {id:'between',     label:'is between',       symbol:'between',          args:2},
  outside:     {id:'outside',     label:'is outside',       symbol:'outside',          args:2},
  empty:       {id:'empty',       label:'has no data',      symbol:'has no data',      args:0},
  notEmpty:    {id:'notEmpty',    label:'has data',         symbol:'has data',         args:0},
  includesAny: {id:'includesAny', label:'includes any of',  symbol:'includes any of',  args:1},
  includesAll: {id:'includesAll', label:'includes all of',  symbol:'includes all of',  args:1},
  excludesAny: {id:'excludesAny', label:'excludes any of',  symbol:'excludes any of',  args:1},
  is:          {id:'is',          label:'is',               symbol:'is',               args:1},
  isNot:       {id:'isNot',       label:'is not',           symbol:'is not',           args:1},
  isAnyOf:     {id:'isAnyOf',     label:'is any of',        symbol:'is any of',        args:1},
  contains:    {id:'contains',    label:'contains',         symbol:'contains',         args:1},
  notContains: {id:'notContains', label:'does not contain', symbol:'does not contain', args:1},
  on:          {id:'on',          label:'is on',            symbol:'on',               args:1},
  before:      {id:'before',      label:'is before',        symbol:'before',           args:1},
  after:       {id:'after',       label:'is after',         symbol:'after',            args:1},
};

export type FilterFieldType = 'metric' | 'items' | 'flag' | 'enum' | 'text' | 'date';

/** Sensible operator sets per type; a catalogue may narrow them. */
export const DEFAULT_OPS_BY_TYPE: {[type in FilterFieldType]: ConditionOp[]} = {
  metric: ['gte', 'gt', 'lte', 'lt', 'eq', 'between', 'outside', 'empty', 'notEmpty'],
  items:  ['includesAny', 'includesAll', 'excludesAny'],
  flag:   ['is'],
  enum:   ['is', 'isNot', 'isAnyOf', 'empty', 'notEmpty'],
  text:   ['contains', 'notContains', 'is', 'isNot', 'empty', 'notEmpty'],
  date:   ['on', 'before', 'after', 'between', 'empty', 'notEmpty'],
};

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface FilterFieldDef<TRow = any, TCtx = any> {
  id: string;
  label: string;
  type: FilterFieldType;
  unit?: 'percent' | 'count';
  ops: ConditionOp[];
  hint?: string;
  /** metric: numeric value, NaN / non-finite = "no data". */
  metric?: (row: TRow, ctx: TCtx) => number;
  /** flag: yes / no. */
  flag?: (row: TRow, ctx: TCtx) => boolean;
  /** text: free text, null = "no data". */
  text?: (row: TRow, ctx: TCtx) => string | null | undefined;
  /** date: anything `new Date()` accepts, null = "no data". */
  date?: (row: TRow, ctx: TCtx) => Date | string | number | null | undefined;
  /** enum: the row's value, matched against `options`. */
  value?: (row: TRow, ctx: TCtx) => string | number | null | undefined;
  /** enum: fixed choices, or resolved from the context (e.g. the loaded items). */
  options?: FilterOption[];
  optionsOf?: (ctx: TCtx) => FilterOption[];
  /** items: the set the row's owner holds (e.g. a scorer's assigned items). */
  itemsOf?: (row: TRow, ctx: TCtx) => Iterable<number> | null | undefined;
  /**
   * items: the row's own item id. When set, a matching owner still only shows the rows for the
   * picked items — one match does not drag in the owner's other rows.
   */
  rowItemId?: (row: TRow, ctx: TCtx) => number | null | undefined;
}

export interface FilterCondition {
  /** Client-side identity so the UI can track chips across edits. */
  id: string;
  field: string | null;
  op: ConditionOp | null;
  value?: any;
  value2?: any;
}

let conditionSeq = 0;
/** Ids only have to be unique within a page session. */
export const newConditionId = (): string => `cond_${++conditionSeq}_${Date.now().toString(36)}`;

export const fieldById = <TRow, TCtx>(fields: FilterFieldDef<TRow, TCtx>[], id: string | null | undefined): FilterFieldDef<TRow, TCtx> | undefined =>
  id ? (fields || []).find(field => field.id === id) : undefined;

/** A threshold counts as set only when it is a finite number. */
export const isThresholdActive = (value: any): boolean =>
  value != null && value !== '' && Number.isFinite(Number(value));

export const normalizeIdList = (ids: any): number[] => {
  const list = Array.isArray(ids) ? ids : (ids == null || ids === '' ? [] : [ids]);
  // `Number(null)` is 0, which would let a blank option read as item 0.
  return list.filter(v => v != null && v !== '').map(Number).filter(n => Number.isFinite(n));
};

const normalizeValueList = (values: any): (string | number)[] => {
  const list = Array.isArray(values) ? values : (values == null || values === '' ? [] : [values]);
  return list.filter(v => v != null && v !== '');
};

const hasText = (value: any): boolean => value != null && String(value).trim() !== '';

/** Milliseconds since epoch, or NaN when the value is not a readable instant. */
export const toEpochMs = (value: any): number => {
  if (value == null || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const text = String(value).trim();
  // `YYYY-MM-DD` from a date input is a local calendar day, not UTC midnight.
  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (dayMatch) return new Date(+dayMatch[1], +dayMatch[2] - 1, +dayMatch[3]).getTime();
  const ms = new Date(text).getTime();
  return Number.isFinite(ms) ? ms : NaN;
};

/** Local calendar day `YYYY-MM-DD` of an instant, or null. */
export const localDayOf = (value: any): string | null => {
  const ms = toEpochMs(value);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** A condition filters nothing until its field, operator and arguments are all set. */
export const isConditionComplete = <TRow, TCtx>(
  condition: FilterCondition,
  fields: FilterFieldDef<TRow, TCtx>[],
): boolean => {
  const field = condition ? fieldById(fields, condition.field) : undefined;
  if (!field || !condition.op) return false;
  const op = CONDITION_OPS[condition.op];
  if (!op || field.ops.indexOf(condition.op) === -1) return false;
  if (op.args === 0) return true;
  switch (field.type) {
    case 'items':  return normalizeIdList(condition.value).length > 0;
    case 'flag':   return condition.value === true || condition.value === false;
    case 'enum':
      if (condition.op === 'isAnyOf') return normalizeValueList(condition.value).length > 0;
      return condition.value != null && condition.value !== '';
    case 'text':   return hasText(condition.value);
    case 'date':
      if (op.args === 2) return Number.isFinite(toEpochMs(condition.value)) && Number.isFinite(toEpochMs(condition.value2));
      return Number.isFinite(toEpochMs(condition.value));
    default: // metric
      if (op.args === 1) return isThresholdActive(condition.value);
      return isThresholdActive(condition.value) && isThresholdActive(condition.value2);
  }
};

const compareNumeric = (value: number, op: ConditionOp, a: number, b: number): boolean => {
  // Ratio-to-percent conversion can turn 0.55 into 55.00000000000001.
  // Use the same equality tolerance at every inclusive or exclusive boundary.
  const equal = (left: number, right: number) => Math.abs(left - right) < 1e-9;
  const lower = Math.min(a, b), upper = Math.max(a, b);
  switch (op) {
    case 'gte':     return value >= a || equal(value, a);
    case 'gt':      return value > a && !equal(value, a);
    case 'lte':     return value <= a || equal(value, a);
    case 'lt':      return value < a && !equal(value, a);
    case 'eq':      return equal(value, a);
    case 'between': return (value >= lower || equal(value, lower)) && (value <= upper || equal(value, upper));
    case 'outside': return (value < lower && !equal(value, lower)) || (value > upper && !equal(value, upper));
    default:        return true;
  }
};

/**
 * Evaluate one condition against a row. Incomplete conditions and unknown fields pass — the
 * caller filters them out with `completeConditions` first; this keeps a half-edited chip from
 * blanking the grid while the leader is still typing.
 */
export const evaluateCondition = <TRow, TCtx>(
  row: TRow,
  condition: FilterCondition,
  fields: FilterFieldDef<TRow, TCtx>[],
  ctx: TCtx,
): boolean => {
  const field = fieldById(fields, condition.field);
  if (!field || !condition.op) return true;
  const op = condition.op;

  switch (field.type) {
    case 'flag':
      return !!field.flag(row, ctx) === !!condition.value;

    case 'items': {
      const held = new Set(normalizeIdList(Array.from(field.itemsOf ? (field.itemsOf(row, ctx) || []) : [])));
      const ids = normalizeIdList(condition.value);
      if (op === 'excludesAny') return !ids.some(id => held.has(id));
      const ownerMatches = op === 'includesAll'
        ? ids.every(id => held.has(id))
        : ids.some(id => held.has(id));
      if (!ownerMatches) return false;
      if (!field.rowItemId) return true;
      const own = field.rowItemId(row, ctx);
      // A different held item must not pull a historical, removed assignment back in.
      return own == null ? true : ids.indexOf(Number(own)) !== -1 && held.has(Number(own));
    }

    case 'enum': {
      const raw = field.value ? field.value(row, ctx) : undefined;
      const present = raw != null && raw !== '';
      if (op === 'empty') return !present;
      if (op === 'notEmpty') return present;
      if (!present) return false;
      const actual = String(raw);
      if (op === 'isAnyOf') return normalizeValueList(condition.value).map(String).indexOf(actual) !== -1;
      const matches = actual === String(condition.value);
      return op === 'isNot' ? !matches : matches;
    }

    case 'text': {
      const raw = field.text ? field.text(row, ctx) : undefined;
      const present = hasText(raw);
      if (op === 'empty') return !present;
      if (op === 'notEmpty') return present;
      if (!present) return op === 'notContains' || op === 'isNot';
      const actual = String(raw).toLowerCase();
      const wanted = String(condition.value).trim().toLowerCase();
      switch (op) {
        case 'contains':    return actual.indexOf(wanted) !== -1;
        case 'notContains': return actual.indexOf(wanted) === -1;
        case 'is':          return actual === wanted;
        case 'isNot':       return actual !== wanted;
        default:            return true;
      }
    }

    case 'date': {
      const ms = toEpochMs(field.date ? field.date(row, ctx) : undefined);
      if (op === 'empty') return !Number.isFinite(ms);
      if (op === 'notEmpty') return Number.isFinite(ms);
      if (!Number.isFinite(ms)) return false;
      if (op === 'on') return localDayOf(ms) === localDayOf(condition.value);
      const a = toEpochMs(condition.value);
      const b = toEpochMs(condition.value2);
      switch (op) {
        case 'before':  return ms < a;
        case 'after':   return ms > a;
        case 'between': return ms >= Math.min(a, b) && ms <= Math.max(a, b);
        default:        return compareNumeric(ms, op, a, b);
      }
    }

    default: { // metric
      const value = field.metric(row, ctx);
      if (op === 'empty') return !Number.isFinite(value);
      if (op === 'notEmpty') return Number.isFinite(value);
      if (!Number.isFinite(value)) return false;
      return compareNumeric(value, op, Number(condition.value), Number(condition.value2));
    }
  }
};

export const completeConditions = <TRow, TCtx>(
  conditions: FilterCondition[],
  fields: FilterFieldDef<TRow, TCtx>[],
): FilterCondition[] => (conditions || []).filter(condition => isConditionComplete(condition, fields));

/** Fold every complete condition through the join mode. No complete conditions → pass. */
export const evaluateConditions = <TRow, TCtx>(
  row: TRow,
  conditions: FilterCondition[],
  joinMode: FilterJoinMode,
  fields: FilterFieldDef<TRow, TCtx>[],
  ctx: TCtx,
): boolean => {
  const complete = completeConditions(conditions, fields);
  if (!complete.length) return true;
  const results = complete.map(condition => evaluateCondition(row, condition, fields, ctx));
  return joinMode === 'OR' ? results.some(Boolean) : results.every(Boolean);
};

export interface DescribeOptions {
  /** Label an enum / items value for the chip; defaults to the raw value. */
  labelFor?: (fieldId: string, value: string | number) => string;
}

const formatDateArg = (value: any): string => {
  const ms = toEpochMs(value);
  if (!Number.isFinite(ms)) return String(value ?? '');
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : new Date(ms).toLocaleString();
};

export const describeCondition = <TRow, TCtx>(
  condition: FilterCondition,
  fields: FilterFieldDef<TRow, TCtx>[],
  options: DescribeOptions = {},
): string => {
  const field = fieldById(fields, condition.field);
  if (!field || !condition.op) return '';
  const op = CONDITION_OPS[condition.op];
  if (!op) return '';
  const labelFor = options.labelFor || ((_id: string, value: string | number) => String(value));

  if (op.args === 0) return `${field.label} ${op.symbol}`;

  switch (field.type) {
    case 'flag':
      return `${field.label} is ${condition.value ? 'yes' : 'no'}`;
    case 'items': {
      const count = normalizeIdList(condition.value).length;
      return `${field.label} ${op.symbol} ${count} item${count === 1 ? '' : 's'}`;
    }
    case 'enum': {
      if (condition.op === 'isAnyOf') {
        const labels = normalizeValueList(condition.value).map(v => labelFor(field.id, v));
        return `${field.label} ${op.symbol} ${labels.join(', ')}`;
      }
      return `${field.label} ${op.symbol} ${labelFor(field.id, condition.value)}`;
    }
    case 'text':
      return `${field.label} ${op.symbol} “${String(condition.value ?? '').trim()}”`;
    case 'date':
      if (op.args === 2) return `${field.label} ${op.symbol} ${formatDateArg(condition.value)} and ${formatDateArg(condition.value2)}`;
      return `${field.label} ${op.symbol} ${formatDateArg(condition.value)}`;
    default: {
      const unit = field.unit === 'percent' ? '%' : '';
      if (op.args === 2) return `${field.label} ${op.symbol} ${condition.value}${unit} and ${condition.value2}${unit}`;
      return `${field.label} ${op.symbol} ${condition.value}${unit}`;
    }
  }
};

export const describeConditions = <TRow, TCtx>(
  conditions: FilterCondition[],
  joinMode: FilterJoinMode,
  fields: FilterFieldDef<TRow, TCtx>[],
  options: DescribeOptions = {},
): string => completeConditions(conditions, fields)
  .map(condition => describeCondition(condition, fields, options))
  .join(` ${joinMode} `);

/** Enum choices for a field in a context — fixed list or resolved. */
export const optionsForField = <TRow, TCtx>(field: FilterFieldDef<TRow, TCtx> | null | undefined, ctx: TCtx): FilterOption[] => {
  if (!field) return [];
  if (field.options) return field.options;
  if (field.optionsOf) return field.optionsOf(ctx) || [];
  return [];
};
